import { BasePaginateParamsSchema } from '@/api/common'
import { z } from 'zod'

// Enum constants - single source of truth
export const PRIORITY_VALUES = {
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
} as const

export const TASK_STATUS_VALUES = {
  DRAFT: 1,
  ASSIGNED: 2,
  IN_PROGRESS: 3,
  PENDING_APPROVAL: 4,
  COMPLETED: 5,
  CANCELLED: 6,
} as const

export const ITEM_STATUS_VALUES = {
  PENDING: 1,
  COUNTING: 2,
  COUNTED: 3,
  APPROVED: 4,
  REJECTED: 5,
} as const

// Flexible schemas that handle both string and number inputs from server
export const CycleCountPrioritySchema = z.coerce.number().min(1)
export const CycleCountTaskStatusSchema = z.coerce.number().min(1).max(6)
export const CycleCountItemStatusSchema = z.coerce.number().min(1).max(5)

export type CycleCountPriority = z.infer<typeof CycleCountPrioritySchema>
export type CycleCountTaskStatus = z.infer<typeof CycleCountTaskStatusSchema>
export type CycleCountItemStatus = z.infer<typeof CycleCountItemStatusSchema>

// Labels derived from constants
export const PriorityLabels = {
  [PRIORITY_VALUES.LOW]: 'ต่ำ',
  [PRIORITY_VALUES.NORMAL]: 'ปกติ',
  [PRIORITY_VALUES.HIGH]: 'สูง',
} as const

export const TaskStatusLabels = {
  [TASK_STATUS_VALUES.DRAFT]: 'ร่าง',
  [TASK_STATUS_VALUES.ASSIGNED]: 'มอบหมายแล้ว',
  [TASK_STATUS_VALUES.IN_PROGRESS]: 'กำลังดำเนินการ',
  [TASK_STATUS_VALUES.PENDING_APPROVAL]: 'รอการอนุมัติ',
  [TASK_STATUS_VALUES.COMPLETED]: 'เสร็จสิ้น',
  [TASK_STATUS_VALUES.CANCELLED]: 'ยกเลิก',
} as const

export const ItemStatusLabels = {
  [ITEM_STATUS_VALUES.PENDING]: 'รอนับ',
  [ITEM_STATUS_VALUES.COUNTING]: 'กำลังนับ',
  [ITEM_STATUS_VALUES.COUNTED]: 'นับแล้ว',
  [ITEM_STATUS_VALUES.APPROVED]: 'อนุมัติแล้ว',
  [ITEM_STATUS_VALUES.REJECTED]: 'ปฏิเสธ',
} as const

// Option generators for UI components
export function getPriorityOptions() {
  return [
    {
      value: PRIORITY_VALUES.LOW.toString(),
      label: PriorityLabels[PRIORITY_VALUES.LOW],
    },
    {
      value: PRIORITY_VALUES.NORMAL.toString(),
      label: PriorityLabels[PRIORITY_VALUES.NORMAL],
    },
    {
      value: PRIORITY_VALUES.HIGH.toString(),
      label: PriorityLabels[PRIORITY_VALUES.HIGH],
    },
  ]
}

export function getTaskStatusFilterOptions() {
  return [
    { value: '', label: 'ทั้งหมด' },
    {
      value: TASK_STATUS_VALUES.DRAFT.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.DRAFT],
    },
    {
      value: TASK_STATUS_VALUES.ASSIGNED.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.ASSIGNED],
    },
    {
      value: TASK_STATUS_VALUES.IN_PROGRESS.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.IN_PROGRESS],
    },
    {
      value: TASK_STATUS_VALUES.PENDING_APPROVAL.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.PENDING_APPROVAL],
    },
    {
      value: TASK_STATUS_VALUES.COMPLETED.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.COMPLETED],
    },
    {
      value: TASK_STATUS_VALUES.CANCELLED.toString(),
      label: TaskStatusLabels[TASK_STATUS_VALUES.CANCELLED],
    },
  ]
}

export function getPriorityFilterOptions() {
  return [{ value: '', label: 'ทั้งหมด' }, ...getPriorityOptions()]
}

// Search Parameters Schema
export const Inv5010SearchParamsSchema = z
  .object({
    number: z.string().optional(),
    status: CycleCountTaskStatusSchema.optional(),
    assignedToUserId: z.coerce.number().optional(),
    priority: CycleCountPrioritySchema.optional(),
    dueBefore: z.coerce.date().optional(),
    showDashboard: z.coerce.boolean().optional(),
  })
  .merge(BasePaginateParamsSchema)

export type Inv5010SearchParams = z.infer<typeof Inv5010SearchParamsSchema>

// Task Item Schema
export const TaskItemSchema = z.object({
  id: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  locationCode: z.string().nullable(),
  lotNo: z.string().nullable(),
  lotDate: z.string().nullable(),
  mold: z.string().nullable(),
  expectedQty: z.number(),
  countedQty: z.number().nullable(),
  notes: z.string().nullable(),
  status: CycleCountItemStatusSchema,
  isClaimItem: z.boolean(),
})

export type TaskItem = z.infer<typeof TaskItemSchema>

// Task List Item Schema
export const TaskListItemSchema = z.object({
  id: z.string(),
  number: z.string(),
  date: z.coerce.date(),
  dueDate: z.coerce.date(),
  priority: CycleCountPrioritySchema,
  status: CycleCountTaskStatusSchema,
  assignedToUserId: z.number().nullable(),
  assignedToUserName: z.string().nullable(),
  startedDate: z.coerce.date().nullable(),
  completedDate: z.coerce.date().nullable(),
  totalItems: z.number(),
  countedItems: z.number(),
  pendingItems: z.number(),
})

export type TaskListItem = z.infer<typeof TaskListItemSchema>

// Task List Response Schema
export const Inv5010SearchResultSchema = z.object({
  data: z.array(TaskListItemSchema),
  pageNumber: z.number(),
  pageSize: z.number(),
  totalPage: z.number(),
})

export type Inv5010SearchResult = z.infer<typeof Inv5010SearchResultSchema>

// Task Detail Schema
export const TaskDetailSchema = z.object({
  id: z.string(),
  number: z.string(),
  date: z.coerce.date(),
  dueDate: z.coerce.date(),
  priority: CycleCountPrioritySchema,
  status: CycleCountTaskStatusSchema,
  assignedToUserId: z.number().nullable(),
  startedDate: z.coerce.date().nullable(),
  completedDate: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  items: z.array(TaskItemSchema),
})

export type TaskDetail = z.infer<typeof TaskDetailSchema>

// Task Form Schema
export const TaskFormSchema = z.object({
  itemCount: z
    .number()
    .min(1, 'กรุณาระบุจำนวนรายการที่มากกว่า 0')
    .max(100, 'จำนวนรายการต้องไม่เกิน 100'),
  priority: z.number().min(1).max(3), // Restricted to supported priorities (LOW, NORMAL, HIGH)
  dueDate: z.date(),
  assignedToUserId: z.number().nullable(),
  notes: z.string().nullable(),
})

export type TaskForm = z.infer<typeof TaskFormSchema>

// Create Task Result Schema
export const CreateTaskResultSchema = z.object({
  taskId: z.string(),
  taskNumber: z.string(),
})

export type CreateTaskResult = z.infer<typeof CreateTaskResultSchema>

// Cycle Count Adjustment Schema
export const CycleCountAdjustmentSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  itemId: z.number(),
  itemCode: z.string(),
  itemDescription: z.string(),
  locationCode: z.string(),
  lotNo: z.string(),
  lotDate: z.coerce.date(),
  mold: z.string(),
  systemQty: z.number(),
  countedQty: z.number(),
  variance: z.number(),
  variancePercent: z.number(),
  adjustedVariance: z.number(),
  adjustedVariancePercent: z.number(),
  approved: z.boolean(),
  rejected: z.boolean(),
  approvalDate: z.coerce.date().nullable(),
  approvalNotes: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  isClaimItem: z.boolean(),
})

export type CycleCountAdjustment = z.infer<typeof CycleCountAdjustmentSchema>

// Task Adjustments Response Schema
export const TaskAdjustmentsResponseSchema = z.object({
  adjustments: z.array(CycleCountAdjustmentSchema),
})

export type TaskAdjustmentsResponse = z.infer<
  typeof TaskAdjustmentsResponseSchema
>

// Approve Adjustment Request Schema
export const ApproveAdjustmentRequestSchema = z.object({
  useAdjustedVariance: z.boolean(),
  notes: z.string().optional(),
})

export type ApproveAdjustmentRequest = z.infer<
  typeof ApproveAdjustmentRequestSchema
>

// Reject Adjustment Request Schema
export const RejectAdjustmentRequestSchema = z.object({
  reason: z.string().min(1, 'กรุณาระบุเหตุผลในการปฏิเสธ'),
})

export type RejectAdjustmentRequest = z.infer<
  typeof RejectAdjustmentRequestSchema
>
